import { clipBoxStateAtom, pointCloudTargetsAtom, type ClipBoxMode } from '@/atoms/viewer.atom';
import { ClipMode, type IClipBox } from '@pnext/three-loader';
import { PivotControls } from '@react-three/drei';
import { useFrame, useThree, type ThreeEvent } from '@react-three/fiber';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

const CLIP_MODE_MAP: Record<Exclude<ClipBoxMode, 'NONE'>, ClipMode> = {
  CLIP_OUTSIDE: ClipMode.CLIP_OUTSIDE,
  HIGHLIGHT_INSIDE: ClipMode.HIGHLIGHT_INSIDE,
  CLIP_INSIDE: ClipMode.CLIP_INSIDE,
};

const MIN_HALF_SIZE = 0.25;

const unitBoxGeometry = new THREE.BoxGeometry(1, 1, 1);

type FaceDef = { axis: 'x' | 'y' | 'z'; sign: 1 | -1 };

const FACES: FaceDef[] = [
  { axis: 'x', sign: 1 },
  { axis: 'x', sign: -1 },
  { axis: 'y', sign: 1 },
  { axis: 'y', sign: -1 },
  { axis: 'z', sign: 1 },
  { axis: 'z', sign: -1 },
];

const AXIS_COLORS: Record<string, string> = { x: '#ff6666', y: '#66ff66', z: '#6688ff' };

function getNDC(event: PointerEvent, domElement: HTMLElement): THREE.Vector2 {
  const rect = domElement.getBoundingClientRect();
  return new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1,
  );
}

export const ClipBoxTool = ({ projectId }: { projectId: string }) => {
  const [state, setState] = useAtom(clipBoxStateAtom(projectId));
  const pointClouds = useAtomValue(pointCloudTargetsAtom);
  const camera = useThree((s) => s.camera);
  const gl = useThree((s) => s.gl);
  const controls = useThree((s) => s.controls) as any;

  const { enabled, clipMode, interactionMode, center, halfSize, rotation } = state;

  // Live transform during drag — avoids atom writes every frame
  const transformRef = useRef({ center, halfSize, rotation });
  const isDragging = useRef(false);

  // Dynamic gizmo scale based on camera distance (updates only on significant change)
  const [gizmoScale, setGizmoScale] = useState(1);
  const lastGizmoScaleRef = useRef(1);

  // Face handle refs and drag state
  const handleRefs = useRef<(THREE.Mesh | null)[]>(new Array(6).fill(null));
  const faceDragRef = useRef<{
    face: FaceDef;
    plane: THREE.Plane;
    startIntersection: THREE.Vector3;
    startCenter: [number, number, number];
    startHalfSize: [number, number, number];
    rotation: [number, number, number, number];
    dragDir: THREE.Vector3;
  } | null>(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const boxGroupRef = useRef<THREE.Group>(null);
  const [pivotRemountKey, setPivotRemountKey] = useState(0);

  // Keep ref in sync with atom state when not dragging
  useEffect(() => {
    if (!isDragging.current) {
      transformRef.current = { center, halfSize, rotation };
    }
  }, [center, halfSize, rotation]);

  // Initialize box when enabled for the first time (center is null)
  useEffect(() => {
    if (!enabled) {
      pointClouds.forEach((pc) => {
        pc.material.clipMode = ClipMode.DISABLED;
        pc.material.useClipBox = false;
        pc.material.setClipBoxes([]);
        pc.material.updateShaderSource();
      });
      return;
    }

    if (center !== null) return; // already initialized

    let target: THREE.Vector3;
    if (controls && 'getTarget' in controls) {
      target = (controls as any).getTarget(new THREE.Vector3());
    } else {
      const dir = new THREE.Vector3();
      camera.getWorldDirection(dir);
      target = camera.position.clone().add(dir.multiplyScalar(20));
    }

    const dist = camera.position.distanceTo(target);
    const hs = Math.min(Math.max(dist * 0.15, 1), 10);

    setState((prev) => ({
      ...prev,
      center: [target.x, target.y, target.z],
      halfSize: [hs, hs, hs],
      rotation: [0, 0, 0, 1],
    }));
  }, [enabled, center, pointClouds, camera, controls, setState]);

  // Apply clip box to point cloud materials each frame
  useFrame(() => {
    if (!enabled) return;

    const t = transformRef.current;
    if (!t.center) return;

    if (clipMode !== 'NONE') {
      const fullSize = new THREE.Vector3(t.halfSize[0] * 2, t.halfSize[1] * 2, t.halfSize[2] * 2);
      const q = new THREE.Quaternion(t.rotation[0], t.rotation[1], t.rotation[2], t.rotation[3]);
      const c = new THREE.Vector3(t.center[0], t.center[1], t.center[2]);

      const matrix = new THREE.Matrix4().compose(c, q, fullSize);
      const inverse = matrix.clone().invert();

      const clipBox: IClipBox = {
        box: new THREE.Box3(new THREE.Vector3(-0.5, -0.5, -0.5), new THREE.Vector3(0.5, 0.5, 0.5)),
        matrix,
        inverse,
        position: c,
      };

      pointClouds.forEach((pc) => {
        pc.material.clipMode = CLIP_MODE_MAP[clipMode];
        pc.material.useClipBox = true;
        pc.material.setClipBoxes([clipBox]);
      });
    } else {
      pointClouds.forEach((pc) => {
        pc.material.clipMode = ClipMode.DISABLED;
        pc.material.useClipBox = false;
        pc.material.setClipBoxes([]);
      });
    }

    // Camera-aware scale factor: ortho uses frustum/zoom, perspective uses distance
    const cCenter = new THREE.Vector3(t.center[0], t.center[1], t.center[2]);
    const isOrtho = (camera as THREE.OrthographicCamera).isOrthographicCamera;
    let viewScaleFactor: number;
    if (isOrtho) {
      const ortho = camera as THREE.OrthographicCamera;
      viewScaleFactor = (ortho.top - ortho.bottom) / ortho.zoom;
    } else {
      viewScaleFactor = camera.position.distanceTo(cCenter);
    }

    // Update gizmo scale when it changes significantly (>30%)
    const gizmoTarget = Math.max(viewScaleFactor * 0.1, 0.5);
    if (Math.abs(gizmoTarget - lastGizmoScaleRef.current) / lastGizmoScaleRef.current > 0.3) {
      lastGizmoScaleRef.current = gizmoTarget;
      setGizmoScale(gizmoTarget);
    }

    // Update face handle positions and scales.
    // When a half-size is smaller than the pivot arrow length, push the sphere out past
    // the arrow tip so it doesn't crowd the translate gizmo.
    const hq = new THREE.Quaternion(t.rotation[0], t.rotation[1], t.rotation[2], t.rotation[3]);
    const minOffset = lastGizmoScaleRef.current * 1.15;
    FACES.forEach((face, i) => {
      const mesh = handleRefs.current[i];
      if (!mesh) return;
      const axisIdx = face.axis === 'x' ? 0 : face.axis === 'y' ? 1 : 2;
      const offsetDist = Math.max(t.halfSize[axisIdx], minOffset);
      const offset = new THREE.Vector3();
      offset[face.axis] = face.sign * offsetDist;
      offset.applyQuaternion(hq);
      mesh.position.set(t.center[0] + offset.x, t.center[1] + offset.y, t.center[2] + offset.z);
      const handleScale = isOrtho ? viewScaleFactor * 0.015 : camera.position.distanceTo(mesh.position) * 0.015;
      mesh.scale.setScalar(handleScale);
    });

    // Update box visual group from transformRef (follows both PivotControls and face drags)
    if (boxGroupRef.current) {
      const bq = new THREE.Quaternion(t.rotation[0], t.rotation[1], t.rotation[2], t.rotation[3]);
      const bc = new THREE.Vector3(t.center[0], t.center[1], t.center[2]);
      const bs = new THREE.Vector3(t.halfSize[0] * 2, t.halfSize[1] * 2, t.halfSize[2] * 2);
      boxGroupRef.current.matrix.compose(bc, bq, bs);
    }
  });

  // Build PivotControls initial matrix from persisted state
  const pivotMatrix = useMemo(() => {
    if (!center) return new THREE.Matrix4();
    const m = new THREE.Matrix4();
    m.compose(
      new THREE.Vector3(center[0], center[1], center[2]),
      new THREE.Quaternion(rotation[0], rotation[1], rotation[2], rotation[3]),
      new THREE.Vector3(halfSize[0] * 2, halfSize[1] * 2, halfSize[2] * 2),
    );
    return m;
  }, [center, halfSize, rotation]);

  const onDrag = useCallback((localMatrix: THREE.Matrix4) => {
    const pos = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    const scl = new THREE.Vector3();
    localMatrix.decompose(pos, quat, scl);

    // Enforce minimum box size
    scl.x = Math.max(scl.x, MIN_HALF_SIZE * 2);
    scl.y = Math.max(scl.y, MIN_HALF_SIZE * 2);
    scl.z = Math.max(scl.z, MIN_HALF_SIZE * 2);

    transformRef.current = {
      center: [pos.x, pos.y, pos.z],
      halfSize: [scl.x / 2, scl.y / 2, scl.z / 2],
      rotation: [quat.x, quat.y, quat.z, quat.w],
    };
  }, []);

  const onDragStart = useCallback(() => {
    isDragging.current = true;
    if (controls && 'enabled' in controls) controls.enabled = false;
  }, [controls]);

  const onDragEnd = useCallback(() => {
    isDragging.current = false;
    if (controls && 'enabled' in controls) controls.enabled = true;
    // Flush live transform to atom (persists to sessionStorage)
    setState((prev) => ({ ...prev, ...transformRef.current }));
  }, [controls, setState]);

  // Face handle drag for per-face resizing
  const startFaceDrag = useCallback(
    (e: ThreeEvent<PointerEvent>, face: FaceDef) => {
      e.stopPropagation();
      isDragging.current = true;
      if (controls && 'enabled' in controls) controls.enabled = false;

      const t = transformRef.current;
      if (!t.center) return;

      const q = new THREE.Quaternion(t.rotation[0], t.rotation[1], t.rotation[2], t.rotation[3]);

      // Face outward direction in world space
      const dragDir = new THREE.Vector3();
      dragDir[face.axis] = face.sign;
      dragDir.applyQuaternion(q).normalize();

      // Handle world position
      const axisIdx = face.axis === 'x' ? 0 : face.axis === 'y' ? 1 : 2;
      const offset = new THREE.Vector3();
      offset[face.axis] = face.sign * t.halfSize[axisIdx];
      offset.applyQuaternion(q);
      const handlePos = new THREE.Vector3(t.center[0], t.center[1], t.center[2]).add(offset);

      // Drag plane via Gram-Schmidt: perpendicular to drag axis, facing camera
      const camDir = camera.getWorldDirection(new THREE.Vector3());
      let planeNormal = camDir.clone().addScaledVector(dragDir, -camDir.dot(dragDir));
      if (planeNormal.lengthSq() < 0.001) {
        planeNormal = camera.up.clone().addScaledVector(dragDir, -camera.up.dot(dragDir));
      }
      planeNormal.normalize();

      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(planeNormal, handlePos);

      raycasterRef.current.setFromCamera(getNDC(e.nativeEvent, gl.domElement), camera);
      const startHit = new THREE.Vector3();
      raycasterRef.current.ray.intersectPlane(plane, startHit);

      faceDragRef.current = {
        face,
        plane,
        startIntersection: startHit.clone(),
        startCenter: [...t.center] as [number, number, number],
        startHalfSize: [...t.halfSize] as [number, number, number],
        rotation: t.rotation,
        dragDir,
      };

      gl.domElement.style.cursor = 'grabbing';

      const onPointerMove = (ev: PointerEvent) => {
        const drag = faceDragRef.current;
        if (!drag) return;

        raycasterRef.current.setFromCamera(getNDC(ev, gl.domElement), camera);
        const hit = new THREE.Vector3();
        if (!raycasterRef.current.ray.intersectPlane(drag.plane, hit)) return;

        // Project delta onto the face's outward axis
        const delta = hit.clone().sub(drag.startIntersection);
        const deltaAlongAxis = delta.dot(drag.dragDir);

        const ai = drag.face.axis === 'x' ? 0 : drag.face.axis === 'y' ? 1 : 2;

        // Half-size grows by half the face movement
        const newHalfSize: [number, number, number] = [...drag.startHalfSize];
        newHalfSize[ai] = Math.max(drag.startHalfSize[ai] + deltaAlongAxis / 2, MIN_HALF_SIZE);

        // Compute actual delta after clamping
        const actualDelta = (newHalfSize[ai] - drag.startHalfSize[ai]) * 2;

        // Center shifts by half the actual movement along the face's outward direction
        const dq = new THREE.Quaternion(drag.rotation[0], drag.rotation[1], drag.rotation[2], drag.rotation[3]);
        const shift = new THREE.Vector3();
        shift[drag.face.axis] = (drag.face.sign * actualDelta) / 2;
        shift.applyQuaternion(dq);

        transformRef.current = {
          center: [drag.startCenter[0] + shift.x, drag.startCenter[1] + shift.y, drag.startCenter[2] + shift.z],
          halfSize: newHalfSize,
          rotation: drag.rotation,
        };
      };

      const onPointerUp = () => {
        faceDragRef.current = null;
        isDragging.current = false;
        gl.domElement.style.cursor = '';
        if (controls && 'enabled' in controls) controls.enabled = true;
        // Flush to atom and remount PivotControls to pick up new matrix
        setState((prev) => ({ ...prev, ...transformRef.current }));
        setPivotRemountKey((k) => k + 1);
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
      };

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    },
    [camera, controls, gl, setState],
  );

  if (!enabled || !center) return null;

  // VIEW mode: hide all clip box visuals (no yellow wireframe/box)
  if (interactionMode === 'VIEW') {
    return null;
  }

  return (
    <>
      <PivotControls
        key={`${interactionMode}-${pivotRemountKey}`}
        matrix={pivotMatrix}
        autoTransform
        scale={gizmoScale}
        depthTest={false}
        disableScaling
        onDrag={onDrag}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        axisColors={['#ff6666', '#66ff66', '#6688ff']}
      />

      {/* Box visuals — positioned imperatively in useFrame so they follow ALL drag sources */}
      <group ref={boxGroupRef} matrixAutoUpdate={false}>
        {/* Wireframe edges */}
        <lineSegments renderOrder={998}>
          <edgesGeometry args={[unitBoxGeometry]} />
          <lineBasicMaterial color='#ffcc00' transparent opacity={0.8} depthTest={false} />
        </lineSegments>

        {/* Backface-only semi-transparent sides — visible only from inside the box */}
        <mesh renderOrder={997}>
          <boxGeometry args={[1, 1, 1]} />
          <meshBasicMaterial color='#ffcc00' transparent opacity={0.08} side={THREE.BackSide} depthTest={false} />
        </mesh>
      </group>

      {/* Face handles for per-face resizing (positioned imperatively in useFrame) */}
      {FACES.map((face, i) => (
        <mesh
          key={`face-${face.axis}-${face.sign}`}
          ref={(el) => {
            handleRefs.current[i] = el;
          }}
          onPointerDown={(e) => startFaceDrag(e, face)}
          onPointerOver={() => {
            if (!faceDragRef.current) gl.domElement.style.cursor = 'grab';
          }}
          onPointerOut={() => {
            if (!faceDragRef.current) gl.domElement.style.cursor = '';
          }}
          renderOrder={999}>
          <sphereGeometry args={[1, 12, 12]} />
          <meshBasicMaterial color={AXIS_COLORS[face.axis]} transparent opacity={0.85} depthTest={false} />
        </mesh>
      ))}
    </>
  );
};
